import { IUser, MeetingConfigViewModel } from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { useUserSession } from '../../../../../models/hooks/appStateHooks';
import { getDisplayName } from '../../../../../models/NameUtils';
import { useLoadAllUsers } from '../../../../../queries/User/useLoadAllUsers';
import { MaxSelectedTeammates } from '../../../../../scheduleMeeting/constants';
import { Button } from '../../../../components-v2/Button';
import {
	Combobox,
	ComboboxContent,
	ComboboxInput,
	ComboboxList,
	ComboboxStatus,
} from '../../../../components-v2/Combobox';
import {
	ComboboxAvatarItem,
	ComboboxAvatarItemAvatar,
	ComboboxAvatarItemDescription,
	ComboboxAvatarItemTitle,
} from '../../../../components-v2/Combobox/ComboboxAvatarItem';
import {
	Dialog,
	DialogBody,
	DialogFooter,
	DialogHeader,
	DialogPopup,
	DialogTitle,
} from '../../../../components-v2/Dialog';
import { LoadingSpinner } from '../../../LoadingSpinner';
import { XIcon } from '../../../svgs/icons/XIcon';
import { baseStyleSheet as bs } from '../../../../styles/styles';
import { styleSheet } from './styles';
import { useStartBooking } from './useStartBooking';

interface IProps {
	meeting: MeetingConfigViewModel;
	isOpen: boolean;
	onClose: () => void;
}

export function BookWithTeammatesModal({ meeting, isOpen, onClose }: IProps) {
	const id = React.useId();
	const userSession = useUserSession();
	const [teammates, setTeammates] = React.useState<IUser[]>([]);
	const [searchValue, setSearchValue] = React.useState('');

	const { users, isLoading: isLoadingUsers, error } = useLoadAllUsers({ pageSize: 500 });

	const hostId = meeting?.forUser?.id ?? userSession?.user?.id;

	const selectableUsers = React.useMemo(() => {
		// Picked teammates stay in the list so the checkbox indicator can show them as selected.
		const query = searchValue.trim().toLowerCase();

		return users
			.filter(user => user.activationStatus === 'ACTIVE')
			.filter(user => user.id !== hostId)
			.filter(
				user =>
					!query || getDisplayName(user).toLowerCase().includes(query) || user.email?.toLowerCase().includes(query)
			)
			.sort((a, b) => getDisplayName(a).localeCompare(getDisplayName(b)));
	}, [users, hostId, searchValue]);

	const handleClose = () => {
		setTeammates([]);
		setSearchValue('');
		onClose();
	};

	const { isPending, startBooking } = useStartBooking(meeting, handleClose);

	const atLimit = teammates.length >= MaxSelectedTeammates;

	const onTeammatesChanged = (value: unknown) => {
		const selected = (value ?? []) as IUser[];

		// Base UI reports the whole selection; ignore anything past the cap rather than silently trimming it.
		if (selected.length > MaxSelectedTeammates) {
			return;
		}

		setTeammates(selected);
		setSearchValue('');
	};

	const removeTeammate = (userId: string) => setTeammates(teammates.filter(x => x.id !== userId));

	const onContinue = () => startBooking(teammates.map(x => x.id));

	let status: React.ReactNode = '';
	if (isLoadingUsers) {
		status = (
			<div className={css(bs.flex, bs.itemsCenter, bs.gap2)}>
				<LoadingSpinner type='extra-tiny' />
				Loading users...
			</div>
		);
	} else if (error) {
		status = 'Error loading users';
	} else if (selectableUsers.length === 0) {
		status = searchValue ? `No users found for "${searchValue}"` : 'No other users to add';
	}

	return (
		<Dialog open={isOpen} onOpenChange={open => (open ? null : handleClose())}>
			<DialogPopup>
				<DialogHeader>
					<DialogTitle>Book with teammates</DialogTitle>
				</DialogHeader>
				<DialogBody>
					<p className={css(styleSheet.description)}>
						The calendar will only show times when you and everyone you add here are available. They&apos;ll be added to
						the invite.
					</p>
					<Combobox
						multiple
						items={selectableUsers}
						value={teammates}
						onValueChange={onTeammatesChanged}
						inputValue={searchValue}
						onInputValueChange={setSearchValue}
						itemToStringLabel={(user: IUser) => getDisplayName(user)}
						isItemEqualToValue={(item: IUser, value: IUser) => item.id === value?.id}
					>
						<ComboboxInput
							disabled={atLimit}
							id={`${id}-teammate-combobox`}
							placeholder={atLimit ? `Up to ${MaxSelectedTeammates} teammates` : 'Search employees...'}
						/>
						<ComboboxContent>
							{status ? <ComboboxStatus>{status}</ComboboxStatus> : null}
							<ComboboxList>
								{(user: IUser) => (
									<ComboboxAvatarItem key={user.id} value={user}>
										<ComboboxAvatarItemAvatar
											imageUrl={user.profilePic}
											fallbackText={user.firstName?.substring(0, 1) + user.lastName?.substring(0, 1)}
										/>
										<ComboboxAvatarItemTitle>{getDisplayName(user)}</ComboboxAvatarItemTitle>
										<ComboboxAvatarItemDescription>{user.email}</ComboboxAvatarItemDescription>
									</ComboboxAvatarItem>
								)}
							</ComboboxList>
						</ComboboxContent>
					</Combobox>
					{teammates.length > 0 ? (
						<div className={css(styleSheet.selectedList)}>
							{teammates.map(teammate => (
								<div className={css(styleSheet.selectedItem)} key={teammate.id}>
									<div className={css(styleSheet.selectedItemDetails)}>
										<span className={css(styleSheet.selectedItemName)}>{getDisplayName(teammate)}</span>
										<span className={css(styleSheet.selectedItemEmail)}>{teammate.email}</span>
									</div>
									<Button
										aria-label={`Remove ${getDisplayName(teammate)}`}
										onClick={() => removeTeammate(teammate.id)}
										size='icon'
										variant='ghost'
									>
										<XIcon height={12} width={12} />
									</Button>
								</div>
							))}
						</div>
					) : null}
				</DialogBody>
				<DialogFooter>
					<Button variant='secondary' onClick={handleClose}>
						Cancel
					</Button>
					<Button disabled={teammates.length === 0} isLoading={isPending} onClick={onContinue} variant='primary'>
						Continue
					</Button>
				</DialogFooter>
			</DialogPopup>
		</Dialog>
	);
}
